import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import QuizAnswerMode from './QuizAnswerMode';
import QuizReviewMode from './QuizReviewMode';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { enrichQuestionsWithDbFields, mapDbQuestionsToStandard } from '../../utils/questionMapper';
import type { Question, Paper, QuizProps } from './types';

export { type QuizProps } from './types';

export default function Quiz(props?: QuizProps) {
  const navigate = useNavigate();
  const { paperId } = useParams<{ paperId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // 模式判断
  const isPreviewMode = searchParams.get('mode') === 'preview';
  const isEditorPreview = searchParams.get('mode') === 'editor-preview' || location.state?.previewData?.isEditorPreview;
  const fromResult = location.state?.fromResult;
  const isProxyView = location.state?.isProxyView;
  const studentName = location.state?.studentName;
  const isPreview = isPreviewMode || isEditorPreview;

  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [paper, setPaper] = useState<Paper | null>(null);
  const [examQuestions, setExamQuestions] = useState<Question[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [reviewData, setReviewData] = useState<{
    answers: Record<string, string>;
    score: number;
    timeLeft: number;
    objectiveScore?: number | null;
    objectiveTotalScore?: number | null;
    programmingScore?: number | null;
    questionFeedbacks?: Record<string, { score?: number; feedback?: string }> | null;
    source?: 'practice' | 'class' | 'exam'; // 新增：来源字段
  } | null>(null);

  // 初始化数据加载
  useEffect(() => {
    const init = async () => {
      setLoading(true);

      // 预览模式：加载试卷数据
      if (isPreviewMode || isEditorPreview) {
        // 从 previewData 获取数据（编辑器预览）
        const previewData = location.state?.previewData;
        if (previewData && previewData.questions) {
          setPaper({
            id: previewData.paperId,
            title: previewData.paperTitle,
            questions: previewData.questions,
            total_score: previewData.totalScore,
            category_type: ''
          });
          setExamQuestions(previewData.questions);
        } else if (paperId) {
          // 从数据库加载（试卷列表/草稿列表跳转）
          // 先尝试从 papers 表查询
          let { data: paperData } = await supabase.from('papers').select('*').eq('id', paperId).single();
          let isTeacherPaper = false;

          // 如果找不到，尝试从 teacher_papers 表查询
          if (!paperData) {
            const { data: teacherPaperData } = await supabase.from('teacher_papers').select('*').eq('id', paperId).single();
            if (teacherPaperData) {
              paperData = teacherPaperData;
              isTeacherPaper = true;
            }
          }

          if (paperData) {
            const questionsTable = isTeacherPaper ? 'teacher_questions' : 'questions';
            const { data: questionsData } = await supabase.from(questionsTable).select('*').eq('paper_id', paperId).order('order_index', { ascending: true });
            const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
            setPaper({ ...(paperData as any), questions: mappedQuestions, total_score: (paperData as any).total_score || 0 });
            setExamQuestions(mappedQuestions);
          }
        }
        setHasPermission(true);
        setLoading(false);
        return;
      }

      // 代查模式（管理员/教师查看学员答题记录）
      if (isProxyView) {
        // 获取当前登录用户（教师）信息
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });
        }

        const resultData = location.state?.resultData;
        if (resultData && resultData.questions && resultData.questions.length > 0) {
          // 从数据库补充布局/缩放字段
          const { data: questionsData } = await supabase
            .from('questions')
            .select('id, content, title, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
            .eq('paper_id', resultData.paperId);

          let enrichedQuestions = resultData.questions;
          if (questionsData) {
            enrichedQuestions = enrichQuestionsWithDbFields(resultData.questions, questionsData);
          }

          setPaper({
            id: resultData.paperId,
            title: resultData.paperTitle,
            questions: enrichedQuestions,
            total_score: resultData.totalScore,
            category_type: ''
          });
          setExamQuestions(enrichedQuestions);
          setReviewData({
            answers: resultData.answers || {},
            score: resultData.score,
            timeLeft: resultData.timeLeft,
            objectiveScore: resultData.objectiveScore,
            objectiveTotalScore: resultData.objectiveTotalScore,
            programmingScore: resultData.programmingScore,
            questionFeedbacks: resultData.questionFeedbacks || null,
            source: (resultData as any).source // 新增：传递来源字段
          });
          setHasPermission(true);
          setLoading(false);
          return;
        }
      }

      // 回顾模式（从答题结果页跳转）
      if (fromResult) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });

          // 从 Edge Function 获取最新答题记录数据
          const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({ action: 'get_records' }),
          });

          const result = await response.json();

          if (result.success && result.records && result.records.length > 0) {
            const currentRecord = result.records.find((r: any) => r.paper_id === paperId);

            if (currentRecord && currentRecord.questions) {
              // 从数据库补充布局/缩放字段
              const { data: questionsData } = await supabase
                .from('questions')
                .select('id, content, title, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
                .eq('paper_id', paperId);

              let enrichedQuestions = currentRecord.questions;
              if (questionsData) {
                enrichedQuestions = enrichQuestionsWithDbFields(currentRecord.questions, questionsData);
              }

              setPaper({
                id: currentRecord.paper_id,
                title: currentRecord.paper_title,
                questions: enrichedQuestions,
                total_score: currentRecord.total_score,
                category_type: ''
              });
              setExamQuestions(enrichedQuestions);
              setReviewData({
                answers: currentRecord.answers || {},
                score: currentRecord.score,
                timeLeft: currentRecord.time_left,
                objectiveScore: currentRecord.objectiveScore ?? currentRecord.objective_score,
                objectiveTotalScore: currentRecord.objectiveTotalScore ?? currentRecord.objective_total_score,
                programmingScore: currentRecord.programmingScore ?? currentRecord.programming_score,
                questionFeedbacks: currentRecord.question_feedbacks || null,
                source: currentRecord.source // 新增：传递来源字段
              });
              setHasPermission(true);
              setLoading(false);
              return;
            }
          }
        }
      }

      // 收藏练习模式已改为页面内切换，不再使用独立路由
      // 如果有人通过旧链接访问，重定向到收藏页面
      if (paperId === 'favorites-practice') {
        navigate('/favorites', { replace: true });
        return;
      }

      // 正常答题模式：检查登录和权限
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
      setCurrentUser({ id: session.user.id, ...profile });

      // 加载试卷数据
      if (paperId) {
        const assignmentId = searchParams.get('assignmentId');

        // 如果有 assignmentId，通过 Edge Function 获取试卷和题目数据（绕过 RLS）
        if (assignmentId && session) {
          try {
            const assignmentResponse = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ action: 'start_assignment', assignmentId }),
            });
            const assignmentResult = await assignmentResponse.json();

            if (assignmentResult.success && assignmentResult.data?.paper && assignmentResult.data?.questions) {
              const paperData = assignmentResult.data.paper;
              const questionsData = assignmentResult.data.questions;
              const enabledTypes = assignmentResult.data.enabledQuestionTypes;

              const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
              let filteredQuestions = mappedQuestions;
              let effectiveTotalScore = paperData.total_score || 0;

              if (enabledTypes) {
                filteredQuestions = mappedQuestions.filter(q => enabledTypes.includes(q.type));
                effectiveTotalScore = filteredQuestions.reduce((sum, q) => sum + q.score, 0);
              }

              setPaper({ ...paperData, questions: filteredQuestions, total_score: effectiveTotalScore });
              setExamQuestions(filteredQuestions);
              setHasPermission(true);
              setLoading(false);
              return;
            }
          } catch (err) {
            console.error('通过 Edge Function 获取作业数据失败:', err);
            // 失败时回退到直接查询数据库
          }
        }

        // 无 assignmentId 或 Edge Function 失败时，直接从数据库查询
        // 先尝试从 papers 表查询（平台试卷）
        let { data: paperData } = await supabase.from('papers').select('*').eq('id', paperId).single();
        if (!paperData) {
          // papers 表中找不到，尝试从 teacher_papers 表查询（教师自建试卷）
          const { data: teacherPaperData } = await supabase.from('teacher_papers').select('*').eq('id', paperId).single();
          if (teacherPaperData) {
            paperData = teacherPaperData;
            const { data: questionsData } = await supabase.from('teacher_questions').select('*').eq('paper_id', paperId).order('order_index', { ascending: true });
            const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
            setPaper({ ...(paperData as any), questions: mappedQuestions, total_score: (paperData as any).total_score || 0 });
            setExamQuestions(mappedQuestions);
          }
        } else if (paperData) {
          const { data: questionsData } = await supabase.from('questions').select('*').eq('paper_id', paperId).order('order_index', { ascending: true });

          const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
          setPaper({ ...(paperData as any), questions: mappedQuestions, total_score: (paperData as any).total_score || 0 });
          setExamQuestions(mappedQuestions);
        }
      }

      setHasPermission(true);
      setLoading(false);
    };

    init();
  }, [navigate, paperId, location.state, isPreviewMode, isEditorPreview, fromResult, isProxyView]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">暂无权限</h2>
          <p className="text-gray-600 mb-6">您没有权限参加此考试，请先购买相应套餐。</p>
          <button onClick={() => navigate('/membership')} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            去购买套餐
          </button>
        </div>
      </div>
    );
  }

  // 预览模式 - 使用 QuizAnswerMode (isPreview=true)
  if (isPreview) {
    if (!paper || examQuestions.length === 0) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">试卷数据加载中...</div>
        </div>
      );
    }
    return (
      <QuizAnswerMode
        paper={paper}
        examQuestions={examQuestions}
        currentUser={currentUser}
        paperId={paperId}
        isPreview={true}
        isEditorPreview={isEditorPreview}
        onBack={handleBack}
      />
    );
  }

  // 回顾模式（从结果页查看或代查模式）
  if (fromResult || isProxyView) {
    if (!paper || !reviewData) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">数据加载失败</div>
        </div>
      );
    }
    return (
      <QuizReviewMode
        paper={paper}
        examQuestions={examQuestions}
        answers={reviewData.answers}
        score={reviewData.score}
        timeLeft={reviewData.timeLeft}
        objectiveScore={reviewData.objectiveScore}
        objectiveTotalScore={reviewData.objectiveTotalScore}
        programmingScore={reviewData.programmingScore}
        questionFeedbacks={reviewData.questionFeedbacks}
        currentUser={currentUser}
        paperId={paperId}
        studentName={studentName}
        source={reviewData.source}
        onBack={handleBack}
      />
    );
  }

  // 正常答题模式
  if (!paper || examQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">试卷加载失败</div>
      </div>
    );
  }

  return (
    <QuizAnswerMode
      paper={paper}
      examQuestions={examQuestions}
      currentUser={currentUser}
      paperId={paperId}
      isPreview={false}
      isEditorPreview={false}
      onBack={handleBack}
    />
  );
}
